import { supabase } from '../../config/supabase';
import { PostgrestError } from '@supabase/supabase-js';

/**
 * SupabaseService - Service cơ bản cho các thao tác CRUD với Supabase
 */
export class SupabaseService {
  
  /**
   * Lấy tất cả records từ một table
   */
  static async findAll<T>(
    tableName: string, 
    options?: {
      select?: string;
      orderBy?: { column: string; ascending?: boolean };
      limit?: number;
      offset?: number;
    }
  ): Promise<{ data: T[] | null; error: PostgrestError | null }> {
    try {
      let query = supabase.from(tableName).select(options?.select || '*');
      
      if (options?.orderBy) {
        query = query.order(options.orderBy.column, { 
          ascending: options.orderBy.ascending ?? true 
        });
      }
      
      if (options?.limit) {
        query = query.limit(options.limit);
      }
      
      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }
      
      const { data, error } = await query;
      
      return { data: data as T[], error };
    } catch (error) {
      console.error(`Error fetching from ${tableName}:`, error);
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Lấy một record theo ID
   */
  static async findById<T>(
    tableName: string, 
    id: string | number,
    options?: {
      select?: string;
      idColumn?: string;
    }
  ): Promise<{ data: T | null; error: PostgrestError | null }> {
    try {
      const idColumn = options?.idColumn || 'id';
      const { data, error } = await supabase
        .from(tableName)
        .select(options?.select || '*')
        .eq(idColumn, id)
        .single();
      
      return { data: data as T, error };
    } catch (error) {
      console.error(`Error fetching ${tableName} by ID:`, error);
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Tạo mới một record
   */
  static async create<T>(
    tableName: string, 
    data: Partial<T>
  ): Promise<{ data: T | null; error: PostgrestError | null }> {
    try {
      const { data: result, error } = await supabase
        .from(tableName)
        .insert(data)
        .select()
        .single();
      
      return { data: result as T, error };
    } catch (error) {
      console.error(`Error creating ${tableName}:`, error);
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Cập nhật một record theo ID
   */
  static async update<T>(
    tableName: string, 
    id: string | number, 
    data: Partial<T>,
    options?: {
      idColumn?: string;
    }
  ): Promise<{ data: T | null; error: PostgrestError | null }> {
    try {
      const idColumn = options?.idColumn || 'id';
      const { data: result, error } = await supabase
        .from(tableName)
        .update(data)
        .eq(idColumn, id)
        .select()
        .single();
      
      return { data: result as T, error };
    } catch (error) {
      console.error(`Error updating ${tableName}:`, error);
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Xóa một record theo ID
   */
  static async delete(
    tableName: string, 
    id: string | number,
    options?: {
      idColumn?: string;
    }
  ): Promise<{ error: PostgrestError | null }> {
    try {
      const idColumn = options?.idColumn || 'id';
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq(idColumn, id);
      
      return { error };
    } catch (error) {
      console.error(`Error deleting ${tableName}:`, error);
      return { error: error as PostgrestError };
    }
  }

  /**
   * Tìm kiếm records với điều kiện
   */
  static async findWhere<T>(
    tableName: string,
    conditions: Array<{ column: string; operator: string; value: any }>,
    options?: {
      select?: string;
      orderBy?: { column: string; ascending?: boolean };
      limit?: number;
    }
  ): Promise<{ data: T[] | null; error: PostgrestError | null }> {
    try {
      let query = supabase.from(tableName).select(options?.select || '*');
      
      conditions.forEach(condition => {
        switch (condition.operator) {
          case 'eq':
            query = query.eq(condition.column, condition.value);
            break;
          case 'neq':
            query = query.neq(condition.column, condition.value);
            break;
          case 'gt':
            query = query.gt(condition.column, condition.value);
            break;
          case 'gte':
            query = query.gte(condition.column, condition.value);
            break;
          case 'lt':
            query = query.lt(condition.column, condition.value);
            break;
          case 'lte':
            query = query.lte(condition.column, condition.value);
            break;
          case 'like':
            query = query.like(condition.column, condition.value);
            break;
          case 'ilike':
            query = query.ilike(condition.column, condition.value);
            break;
          case 'in':
            query = query.in(condition.column, condition.value);
            break;
          case 'is':
            query = query.is(condition.column, condition.value);
            break;
          default:
            query = query.eq(condition.column, condition.value);
        }
      });
      
      if (options?.orderBy) {
        query = query.order(options.orderBy.column, { 
          ascending: options.orderBy.ascending ?? true 
        });
      }
      
      if (options?.limit) {
        query = query.limit(options.limit);
      }
      
      const { data, error } = await query;
      
      return { data: data as T[], error };
    } catch (error) {
      console.error(`Error searching ${tableName}:`, error);
      return { data: null, error: error as PostgrestError };
    }
  }

  /**
   * Đếm số records
   */
  static async count(
    tableName: string,
    conditions?: Array<{ column: string; operator: string; value: any }>
  ): Promise<{ count: number | null; error: PostgrestError | null }> {
    try {
      let query = supabase.from(tableName).select('*', { count: 'exact', head: true });
      
      if (conditions) {
        conditions.forEach(condition => {
          switch (condition.operator) {
            case 'eq':
              query = query.eq(condition.column, condition.value);
              break;
            case 'neq':
              query = query.neq(condition.column, condition.value);
              break;
            // Thêm các operators khác nếu cần
            default:
              query = query.eq(condition.column, condition.value);
          }
        });
      }
      
      const { count, error } = await query;
      
      return { count, error };
    } catch (error) {
      console.error(`Error counting ${tableName}:`, error);
      return { count: null, error: error as PostgrestError };
    }
  }

  /**
   * Upload file lên Supabase Storage
   */
  static async uploadFile(
    bucketName: string,
    fileName: string,
    file: File,
    options?: {
      contentType?: string;
      cacheControl?: string;
      upsert?: boolean;
    }
  ) {
    try {
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(fileName, file, {
          contentType: options?.contentType || file.type,
          cacheControl: options?.cacheControl || '3600',
          upsert: options?.upsert || false
        });
      
      return { data, error };
    } catch (error) {
      console.error('Error uploading file:', error);
      return { data: null, error };
    }
  }

  /**
   * Lấy URL public của file
   */
  static getPublicUrl(bucketName: string, fileName: string) {
    try {
      const { data } = supabase.storage
        .from(bucketName)
        .getPublicUrl(fileName);
      
      return data.publicUrl;
    } catch (error) {
      console.error('Error getting public URL:', error);
      return null;
    }
  }

  /**
   * Xóa file từ Storage
   */
  static async deleteFile(bucketName: string, fileName: string) {
    try {
      const { error } = await supabase.storage
        .from(bucketName)
        .remove([fileName]);
      
      return { error };
    } catch (error) {
      console.error('Error deleting file:', error);
      return { error };
    }
  }
}
